/**
 * 统一错误提示工具：展示错误信息 + TraceId（含快捷复制按钮）
 * - 30秒后自动关闭，用户也可手动点 × 关闭
 * - TraceId 旁边有复制按钮，点击复制到剪贴板
 * - 有 TraceId 的错误自动静默提交工单（用户无感知）
 * - 同一条错误消息 3 秒内不重复弹出
 */
import { Message } from 'element-ui'
import axios from 'axios'
import { get } from '@/utils/local'

// 去重：同一消息 3 秒内不重复弹出
const recentMessages = new Map()
const DEDUP_MS = 3000

/**
 * 从 axios error 对象中提取 TraceId
 * 优先从响应体 data.traceId，其次从响应头 x-trace-id
 */
export function extractTraceId(err) {
  if (!err || !err.response) return ''
  const fromBody = err.response.data && err.response.data.traceId
  const fromHeader = err.response.headers && err.response.headers['x-trace-id']
  return fromBody || fromHeader || ''
}

/**
 * 显示带 TraceId 的错误提示
 * @param {string} message 错误信息
 * @param {string} traceId TraceId（为空则不展示）
 * @param {number} [httpStatus] HTTP 状态码，仅 500 才自动提交工单（业务验证错误如 400/403 不提交）
 */
export function showTraceError(message, traceId, httpStatus) {
  // 去重：同一消息短时间内不重复弹出
  const dedupKey = message + (traceId || '')
  const now = Date.now()
  if (recentMessages.has(dedupKey) && now - recentMessages.get(dedupKey) < DEDUP_MS) {
    return
  }
  recentMessages.set(dedupKey, now)
  // 清理过期记录，防止内存泄漏
  if (recentMessages.size > 50) {
    for (const [key, time] of recentMessages) {
      if (now - time > DEDUP_MS) recentMessages.delete(key)
    }
  }

  if (!traceId) {
    Message.error({
      message: message,
      showClose: true,
      duration: 3000
    })
    return
  }

  // 仅服务端异常（500）才自动提交工单，业务验证错误（400/403等）不提交
  if (!httpStatus || httpStatus >= 500) {
    silentSubmitWorkOrder(message, traceId)
  }

  // 生成唯一 ID 用于绑定复制按钮事件
  const btnId = 'copy-trace-' + Date.now()

  Message.error({
    dangerouslyUseHTMLString: true,
    message: `<span>${escapeHtml(message)}</span>
      <div style="margin-top:6px;font-size:12px;color:#909399;">
        链路ID：<code style="background:#f5f5f5;padding:1px 6px;border-radius:3px;user-select:all;">${escapeHtml(traceId)}</code>
        <button id="${btnId}" style="margin-left:4px;padding:1px 6px;font-size:11px;border:1px solid #dcdfe6;border-radius:3px;background:#fff;cursor:pointer;color:#606266;">复制</button>
      </div>`,
    showClose: true,
    duration: 30000,
    onClose: () => {}
  })

  // 异步绑定复制事件（DOM 需要在下一帧渲染完成）
  setTimeout(() => {
    const btn = document.getElementById(btnId)
    if (btn) {
      btn.addEventListener('click', (e) => {
        e.stopPropagation()
        copyToClipboard(traceId)
        btn.textContent = '已复制'
        btn.style.color = '#67C23A'
        btn.style.borderColor = '#67C23A'
        setTimeout(() => {
          btn.textContent = '复制'
          btn.style.color = '#606266'
          btn.style.borderColor = '#dcdfe6'
        }, 1500)
      })
    }
  }, 100)
}

/**
 * 复制文本到剪贴板
 */
function copyToClipboard(text) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).catch(() => fallbackCopy(text))
  } else {
    fallbackCopy(text)
  }
}

/**
 * 降级复制方案（不支持 clipboard API 时）
 */
function fallbackCopy(text) {
  const textarea = document.createElement('textarea')
  textarea.value = text
  textarea.style.position = 'fixed'
  textarea.style.left = '-9999px'
  document.body.appendChild(textarea)
  textarea.select()
  document.execCommand('copy')
  document.body.removeChild(textarea)
}

/**
 * HTML 转义，防止 XSS
 */
function escapeHtml(str) {
  const div = document.createElement('div')
  div.appendChild(document.createTextNode(str))
  return div.innerHTML
}

/**
 * 静默提交工单（后台自动创建，用户无感知，失败也不提示）
 * @param {string} errorMessage 错误信息
 * @param {string} traceId 链路ID
 */
function silentSubmitWorkOrder(errorMessage, traceId) {
  try {
    const baseUrl = process.env.NODE_ENV === 'production'
      ? window.globalConfig.rootUrl
      : process.env.VUE_APP_BASE_URL
    const tokenObj = get('token')
    const accessToken = tokenObj && tokenObj.access_token ? tokenObj.access_token : ''

    // fire-and-forget，不 await，不处理结果
    axios.post(`${baseUrl}/api/app/work-order`, {
      title: `[系统报错] ${errorMessage.substring(0, 150)}`,
      tag: 0,
      traceId: traceId,
      content: `<p><strong>错误信息：</strong>${escapeHtml(errorMessage)}</p>`
        + `<p><strong>链路ID：</strong>${escapeHtml(traceId)}</p>`
        + `<p><strong>页面地址：</strong>${escapeHtml(window.location.href)}</p>`
        + `<p><strong>发生时间：</strong>${new Date().toLocaleString('zh-CN')}</p>`,
      remark: ''
    }, {
      headers: {
        'Authorization': accessToken ? `Bearer ${accessToken}` : '',
        'Content-Type': 'application/json'
      },
      timeout: 5000
    }).catch(() => {})
  } catch {
    // 静默失败，不做任何处理
  }
}
